import type { Delivery, ServiceRequest } from '@secretario/shared';
import { requestService } from './api/deliveries.js';

/**
 * Preparo de um documento pedido pela conversa. Roda fora de qualquer tela:
 * a pessoa pode fechar o pedido rápido ou mudar de página enquanto o documento
 * é preparado (leva de um segundo a cerca de meio minuto). Quando fica pronto,
 * avisa quem estiver ouvindo (o palco mostra o cartão "Seu documento está pronto").
 */
export const DELIVERY_READY_EVENT = 'secretario:delivery-ready';

type Job = { promise: Promise<Delivery>; startedAt: number };

const jobs = new Map<string, Job>();
const DONE_KEY = 'secretario.documentos.prontos';

type Done = Record<string, { id: string; title: string }>;

function doneMap(): Done {
  try {
    const raw = window.localStorage.getItem(DONE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Done) : {};
  } catch {
    return {};
  }
}

/** O documento que este pedido já gerou (fica guardado: o cartão mostra "Pronto", não pede de novo). */
export function completedDelivery(activityId: string): { id: string; title: string } | null {
  return doneMap()[activityId] ?? null;
}

function rememberDone(activityId: string, delivery: Delivery): void {
  const next = { ...doneMap(), [activityId]: { id: delivery.id, title: delivery.title } };
  const keys = Object.keys(next).slice(-100);
  try {
    window.localStorage.setItem(
      DONE_KEY,
      JSON.stringify(Object.fromEntries(keys.map((key) => [key, next[key]!]))),
    );
  } catch {
    /* sem armazenamento: o cartão volta a oferecer o preparo */
  }
}

export function startDelivery(activityId: string, request: ServiceRequest): Promise<Delivery> {
  const running = jobs.get(activityId);
  if (running) return running.promise;
  const promise = requestService(request).then((delivery) => {
    rememberDone(activityId, delivery);
    window.dispatchEvent(new CustomEvent<Delivery>(DELIVERY_READY_EVENT, { detail: delivery }));
    return delivery;
  });
  // Terminou (bem ou mal): o próximo toque pode pedir de novo.
  promise.finally(() => jobs.delete(activityId)).catch(() => {});
  jobs.set(activityId, { promise, startedAt: Date.now() });
  return promise;
}

export function deliveryInProgress(activityId: string): boolean {
  return jobs.has(activityId);
}
